/* eslint-enable */
import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import { OrderDirection } from '../common/enums/order-direction.enum';
import { useErrorToast } from '../hooks/useToast';
import Card from '../layouts/CardLayout';
import LoadingContent from './LoadingContent';
import { SelectItem } from './Select';
import CellLayout from './CellLayout';
import PageHeader from './PageHeader';
import PageLayout from '../layouts/PageLayout';
import Popover from './Popover';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useAddCompanyMutation,
  useCompaniesQuery,
  useUpdateCompanyMutation,
} from '../services/companies/companies.service';
import { CompanyStatus, ICompany, ICompanyDTO } from '../common/interfaces/company.interface';
import { CompanyTableBasicProps } from './ComapniesTableWithQueryParams';
import DataTableFilters from './DataTableFilters';
import CompaniesStatusSelect from './CompaniesStatusSelect';
import i18n from '../common/config/i18n';
import {
  CompanyStatusBackgroundColorMapper,
  CompanyStatusMarkerColorMapper,
} from '../common/utils/utils';
import Chip from './Chip';
import StatusWithMarker from './StatusWithMarker';
import Button from './Button';
import CompanyModal from './CompanyModal';

const CompanyTableFilteredHeader: TableColumn<ICompany>[] = [
  {
    id: 'id',
    name: 'ID',
    sortable: true,
    grow: 0.15,
    cell: (row: ICompany) => (
      <CellLayout>
        <small id={`company_id__${row.id}`} className="cell-layout">
          {row.id}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'name',
    name: 'Nume',
    grow: 1,
    sortable: true,
    cell: (row: ICompany) => (
      <CellLayout>
        <small id={`company_name__${row.id}`} className="cell-layout">
          {row.name}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
    grow: 1,
    cell: (row: ICompany) => (
      <CellLayout>
        <Chip bgColor={CompanyStatusBackgroundColorMapper[row.status]}>
          <StatusWithMarker
            markerColour={CompanyStatusMarkerColorMapper[row.status]}
            textColour={CompanyStatusBackgroundColorMapper[row.status]}
            id={`company_status__${row.id}`}
          >
            {i18n.t(`companies:status.${row.status}`)}
          </StatusWithMarker>
        </Chip>
      </CellLayout>
    ),
  },
  {
    id: 'contact',
    name: 'Contact',
    sortable: true,
    grow: 1,
    cell: (row: ICompany) => (
      <CellLayout>
        <small id={`company_contact__${row.id}`} className="cell-layout">
          {row.contact}
        </small>
      </CellLayout>
    ),
  },
];

type ComapniesTableType<T extends object> = React.FC<CompanyTableBasicProps> & {
  DataTableComponent: React.FC<DataTableProps<T>>;
};

const CompanyTable: ComapniesTableType<ICompany> = ({
  query,
  setQuery,
}: CompanyTableBasicProps) => {
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState<boolean>(false);
  const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState<boolean>(false);

  const { mutateAsync: addCompany } = useAddCompanyMutation();
  const { mutateAsync: updateCompany } = useUpdateCompanyMutation();

  const [status, setStatus] = useState<SelectItem<string>>();
  const { t } = useTranslation();

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    error: companiesError,
    refetch,
  } = useCompaniesQuery(
    query?.limit as number,
    query?.page as number,
    query.search,
    query.orderBy,
    query.orderDirection as OrderDirection,
    query.status,
  );

  const navigate = useNavigate();

  const onRowClick = (row: ICompany) => {
    navigate(`${row.id}`);
  };

  useEffect(() => {
    if (companiesError) useErrorToast('Eroare la preluarea datelor.');
  }, [companiesError]);

  const buildcompanyActionColumn = (): TableColumn<ICompany> => {
    const opencompanyMenuItems = [
      {
        label: 'Editeaza',
        onClick: handleOnEdit,
        itemId: 'request_open__kebab_button',
      },
    ];

    return {
      name: '',
      cell: (row: ICompany) => (
        <Popover<ICompany>
          row={row}
          items={opencompanyMenuItems}
          popoverId={`company_${row.id}__kebab_menu`}
          className="right-0"
        />
      ),
      width: '60px',
      // allowOverflow: true,
    };
  };

  const handleOnEdit = (row: ICompany) => {
    setSelectedCompany(row);
    setIsEditCompanyModalOpen(true);
  };

  const onChangeRowsPerPage = (limit: number) => {
    setQuery({ limit, page: 1 }, 'replaceIn');
  };

  const onChangePage = (page: number) => {
    setQuery({ page }, 'replaceIn');
  };

  const onSort = (column: TableColumn<ICompany>, direction: SortOrder) => {
    setQuery(
      {
        orderBy: column.id as string,
        orderDirection:
          direction.toLocaleUpperCase() === OrderDirection.ASC
            ? OrderDirection.ASC
            : OrderDirection.DESC,
      },
      'replaceIn',
    );
  };

  const onSearch = (search: string) => {
    setQuery({ search }, 'replaceIn');
  };

  const onResetFilters = () => {
    setStatus(undefined);
    setQuery({}, 'push');
  };

  const onStatusChange = (statusItem: SelectItem<string>) => {
    setStatus(statusItem);
    setQuery({ status: statusItem.key as CompanyStatus }, 'replaceIn');
  };

  const handleOnAddRequest = async (data: ICompanyDTO) => {
    addCompany(
      { ...data },
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          useErrorToast('Eroare la adaugarea companiei');
        },
        onSettled: () => setIsAddCompanyModalOpen(false),
      },
    );
  };

  const handleOnEditRequest = async (data: ICompanyDTO) => {
    updateCompany(
      { ...data, status: data.status ? CompanyStatus.ACTIVE : CompanyStatus.INACTIVE },
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          useErrorToast('Eroare la actualizarea companiei');
        },
        onSettled: () => setIsEditCompanyModalOpen(false),
      },
    );
  };

  return (
    <PageLayout>
      <div className="flex gap-4 justify-between w-full">
        <PageHeader title="Companii" />
        <Button
          id="project_add__button"
          className="btn-primary"
          onClick={setIsAddCompanyModalOpen.bind(null, true)}
          label={'Adauga companie'}
        />
      </div>
      <Card>
        <DataTableFilters
          onSearch={onSearch}
          onResetFilters={onResetFilters}
          searchValue={query.search}
        >
          <CompaniesStatusSelect
            id="request_status_filter__select"
            onChange={onStatusChange}
            selected={status}
            className="max-h-52"
          />
        </DataTableFilters>
        <CompanyTable.DataTableComponent
          columns={
            [...CompanyTableFilteredHeader, buildcompanyActionColumn()] as TableColumn<ICompany>[]
          }
          pagination
          onSort={onSort}
          onRowClicked={onRowClick}
          data={companies?.items}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          pointerOnHover={true}
          paginationDefaultPage={query.page}
          paginationPerPage={query.limit}
          paginationTotalRows={companies?.meta.totalItems}
          defaultSortAsc={false}
          loading={isCompaniesLoading}
        />
        {isCompaniesLoading && <LoadingContent />}
      </Card>
      {isAddCompanyModalOpen && (
        <CompanyModal
          onClose={setIsAddCompanyModalOpen.bind(null, false)}
          onSubmit={handleOnAddRequest}
          title={t('company_modal:form_name')}
          buttonLabel={t('company_modal:add')}
        />
      )}
      {isEditCompanyModalOpen && (
        <CompanyModal
          onClose={setIsEditCompanyModalOpen.bind(null, false)}
          onSubmit={handleOnEditRequest}
          title={t('company_modal:form_name')}
          buttonLabel={t('company_modal:edit')}
          company={selectedCompany}
        />
      )}
    </PageLayout>
  );
};

CompanyTable.DataTableComponent = DataTableComponent;

export default CompanyTable;
